/**
 * The motion system, declared once.
 *
 * This is an Operate surface — a dense internal data product inspected
 * repeatedly — so state change has to be legible without ever being something
 * a reader waits through. Every duration exists twice on purpose: as a TS
 * number for the handful of timings JavaScript owns (a debounce, a live-region
 * delay), and as a CSS custom property for everything CSS owns. The custom
 * properties are what let ONE `prefers-reduced-motion` block in
 * `Cresultcard.css` neutralise the whole system instead of thirty scattered
 * ones.
 *
 * Expand and retract are the same duration and the same curve. The 0.7× exit
 * rule applies to overlays only — an accelerate-away curve on a collapsing
 * height overshoots, and asymmetric retraction is the defect this replaces.
 */

/* ── Durations, in milliseconds ───────────────────────────────────────────── */

export const CRC_D_TAP = 120;
export const CRC_D_HOVER = 160;
export const CRC_D_ENTER = 200;
/** Overlay exit only: 0.7× its enter. Never a height retraction. */
export const CRC_D_EXIT = 140;
/** Disclosure, read-more, action-group tray — the same in both directions. */
export const CRC_D_REVEAL = 240;
export const CRC_D_MOVE = 260;
export const CRC_D_MEDIA = 320;
export const CRC_D_DRAW = 400;
export const CRC_D_FILL = 480;

/** Per-item entrance delay, and the number of items that receive one at all. */
export const CRC_STAGGER_STEP = 24;
export const CRC_STAGGER_MAX = 6;

/** The equaliser loop — the one permitted looping animation in the kit. */
export const CRC_D_EQUALIZER = 760;
/** The buffering sweep along an empty track. Waiting, not broken; no spinner. */
export const CRC_D_BUFFER_SWEEP = 1200;
/** Playback state is announced on a debounce, never per time tick. */
export const CRC_PLAYBACK_ANNOUNCE_DEBOUNCE = 250;

/* ── Easing ───────────────────────────────────────────────────────────────── */

export const CRC_E_SETTLE = "cubic-bezier(0.32, 0.72, 0, 1)";
export const CRC_E_EXIT = "cubic-bezier(0.4, 0, 1, 1)";
export const CRC_E_TAP = "cubic-bezier(0.2, 0, 0, 1)";

/* ── The same tokens, as the CSS custom properties every transition reads ─── */

/**
 * Inline `transition` strings quote these rather than interpolating the numbers
 * above, because a number baked into a style object cannot be overridden by a
 * media query. Reduced motion rewrites the properties; every transition in the
 * kit collapses with them.
 */
export enum CrcDuration {
  Tap = "var(--crc-d-tap)",
  Hover = "var(--crc-d-hover)",
  Enter = "var(--crc-d-enter)",
  Exit = "var(--crc-d-exit)",
  Reveal = "var(--crc-d-reveal)",
  Move = "var(--crc-d-move)",
  Media = "var(--crc-d-media)",
  Draw = "var(--crc-d-draw)",
  Fill = "var(--crc-d-fill)",
}

export enum CrcEase {
  Settle = "var(--crc-e-settle)",
  Exit = "var(--crc-e-exit)",
  Tap = "var(--crc-e-tap)",
}

/** The properties the kit is allowed to animate. Compositor-friendly only. */
export enum CrcAnimatedProperty {
  Opacity = "opacity",
  Transform = "transform",
  BackgroundColor = "background-color",
  BoxShadow = "box-shadow",
  Color = "color",
  GridTemplateRows = "grid-template-rows",
  StrokeDashoffset = "stroke-dashoffset",
}

interface CrcTransitionInput {
  readonly properties: readonly CrcAnimatedProperty[];
  readonly duration: CrcDuration;
  readonly ease: CrcEase;
}

/** One transition string, built from tokens so no call site types a number. */
export function crcTransition({
  properties,
  duration,
  ease,
}: CrcTransitionInput): string {
  return properties
    .map((property) => `${property} ${duration} ${ease}`)
    .join(", ");
}

interface CrcStaggerInput {
  readonly index: number;
}

/**
 * The set's own entrance stagger. Past `CRC_STAGGER_MAX` every remaining item
 * arrives with the last delay: a fortieth row waiting a second to appear is a
 * page that feels broken, not a page that feels choreographed.
 */
export function crcStaggerDelayMs({ index }: CrcStaggerInput): number {
  return Math.min(index, CRC_STAGGER_MAX) * CRC_STAGGER_STEP;
}
